'use client'

import { edit_guide_tile_config } from '@/lib/types'
import React, { createContext, useContext, useMemo, useCallback, useState } from 'react'
import { useDashboardEditorStore } from '@/components/Dashboard/stores/useDashboardEditorStore'

type AppContextType = {
  isEditMode: boolean
  setIsEditMode: (enabled: boolean) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  // Set when a shortcut elsewhere in the app (e.g. an Inside Volya signal)
  // seeds the search bar and only Series results make sense.
  searchSeriesOnly: boolean
  searchRequestId: number
  openSeriesSearch: (query: string) => void
  clearSearchSeriesOnly: () => void
  newDashboardDisplayIds: string[]
  setNewDashboardDisplayIds: (ids: string[]) => void
  addDisplayIdToNewDashboard: (id: string) => void
  currentDashboardDisplayIds: string[]
  setCurrentDashboardDisplayIds: (ids: string[] | ((prev: string[]) => string[])) => void
  addDisplayIdToCurrentDashboard: (id: string) => void
  guideTiles: edit_guide_tile_config[]
  setGuideTiles: React.Dispatch<React.SetStateAction<edit_guide_tile_config[]>>
}

const AppContext = createContext<AppContextType>({
  isEditMode: false,
  setIsEditMode: () => { },
  searchQuery: '',
  setSearchQuery: () => { },
  searchSeriesOnly: false,
  searchRequestId: 0,
  openSeriesSearch: () => { },
  clearSearchSeriesOnly: () => { },
  newDashboardDisplayIds: [],
  setNewDashboardDisplayIds: () => { },
  addDisplayIdToNewDashboard: () => { },
  currentDashboardDisplayIds: [],
  setCurrentDashboardDisplayIds: () => { },
  addDisplayIdToCurrentDashboard: () => { },
  guideTiles: [],
  setGuideTiles: () => { }
})

export function AppProvider({ children }: { children: React.ReactNode }) {
  const isEditMode = useDashboardEditorStore((state) => state.isEditMode)
  const setEditMode = useDashboardEditorStore((state) => state.setEditMode)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchSeriesOnly, setSearchSeriesOnly] = useState<boolean>(false)
  const [searchRequestId, setSearchRequestId] = useState<number>(0)
  const [newDashboardDisplayIds, setNewDashboardDisplayIds] = useState<string[]>([])
  const currentDashboardDisplayIds = useDashboardEditorStore((state) => state.currentDashboardDisplayIds)
  const setCurrentDashboardIds = useDashboardEditorStore((state) => state.setCurrentDashboardDisplayIds)
  const addCurrentDashboardId = useDashboardEditorStore((state) => state.addDisplayIdToCurrentDashboard)
  const guideTiles = useDashboardEditorStore((state) => state.guideTiles)
  const setGuideTilesState = useDashboardEditorStore((state) => state.setGuideTiles)

  const handleSetIsEditMode = useCallback(
    (enabled: boolean) => {
      setEditMode(enabled)
    },
    [setEditMode],
  )


  const openSeriesSearch = useCallback(
    (query: string) => {
      setSearchSeriesOnly(true)
      setSearchQuery(query)
      // Re-clicking the same signal leaves searchQuery unchanged, so the search
      // bar needs its own signal to re-open and re-focus on.
      setSearchRequestId((id) => id + 1)
    },
    [],
  )

  const clearSearchSeriesOnly = useCallback(() => {
    setSearchSeriesOnly(false)
  }, [])

  const addDisplayIdToNewDashboard = useCallback(
    (id: string) => {
      setNewDashboardDisplayIds(prev => (prev.includes(id) ? prev : [...prev, id]))
    },
    [setNewDashboardDisplayIds],
  )

  const handleSetCurrentDashboardDisplayIds = useCallback(
    (updater: string[] | ((prev: string[]) => string[])) => {
      setCurrentDashboardIds(updater)
    },
    [setCurrentDashboardIds],
  )

  const addDisplayIdToCurrentDashboard = useCallback(
    (id: string) => {
      addCurrentDashboardId(id)
    },
    [addCurrentDashboardId],
  )

  const handleSetGuideTiles = useCallback(
    (updater: React.SetStateAction<edit_guide_tile_config[]>) => {
      setGuideTilesState(updater)
    },
    [setGuideTilesState],
  )

  const contextValue = useMemo(
    () => ({
      isEditMode,
      setIsEditMode: handleSetIsEditMode,
      searchQuery,
      setSearchQuery,
      searchSeriesOnly,
      searchRequestId,
      openSeriesSearch,
      clearSearchSeriesOnly,
      newDashboardDisplayIds,
      setNewDashboardDisplayIds,
      addDisplayIdToNewDashboard,
      currentDashboardDisplayIds,
      setCurrentDashboardDisplayIds: handleSetCurrentDashboardDisplayIds,
      addDisplayIdToCurrentDashboard,
      guideTiles,
      setGuideTiles: handleSetGuideTiles,
    }),
    [
      addDisplayIdToCurrentDashboard,
      addDisplayIdToNewDashboard,
      clearSearchSeriesOnly,
      currentDashboardDisplayIds,
      guideTiles,
      openSeriesSearch,
      searchRequestId,
      searchSeriesOnly,
      handleSetCurrentDashboardDisplayIds,
      handleSetGuideTiles,
      handleSetIsEditMode,
      isEditMode,
      newDashboardDisplayIds,
      searchQuery,
      setNewDashboardDisplayIds,
      setSearchQuery,
    ],
  )

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  )
}

export const useApp = () => {
  const context = useContext(AppContext)
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}
